<template>
  <div
    id="tenant-project-tech-comment"
    class="tenant-project-tech-comment"
  >
    <div class="title-area clearfix">
      <div class="guideline-title">
        {{ $t('tenantProjects.submission.comment.technicalReview') }}
      </div>
      <div class="records">
        <span
          v-if="lastTechCommentActionText && (isPic || isSubmitted)"
          class="update-history__upper-text"
        >
          <span
            v-if="!isPic"
            class="font-gray"
          >
            {{ underReviewText }}
          </span>
        </span>
        <div
          v-if="lastTechCommentActionText && (isPic || isSubmitted)"
          class="update-history__upper-text"
        >
          <span class="font-gray">{{ lastTechCommentActionText }}</span>
        </div>
        <i
          v-if="isStarted"
          class="font-gray"
        >
          {{ reviewStartedOn }}
        </i>
      </div>
    </div>
    <div v-if="!isStarted">
      <div
        v-if="isShowFitoutCommentBtn || isShowReinstatementCommentBtn"
        class="start-btn-wrapper"
      >
        <button
          class="start-btn btn--darkblue"
          @click="startTechReview"
        >
          <span v-if="isShowReinstatementCommentBtn">{{ $t('action.startReview') }}</span>
          <span v-else-if="!isDesignCommentDisapproved">{{ deep(activeSubmission, 'designComment') ? $t('action.comment') : $t('action.startReview') }}</span>
          <span v-else>{{ $t('action.edit') }}</span>
        </button>
      </div>
      <div v-else>
        <p class="tech-comment-not-started">
          {{ $t('tenantProjects.submission.comment.techNotStarted') }}.
        </p>
      </div>
    </div>

    <div v-else>
      <div
        v-if="(!isPic && !isSubmitted) && !isAllowablePM"
        class="under-review-hint__text"
      >
        {{ underReviewText }}
      </div>

      <div v-else>
        <div class="closing margin-bottom">
          <DescriptionTinymceEditor
            v-if="canEditingEditor"
            ref="greetingEditor"
            :key="`${activeSubmission.id}_greetingEditor`"
            v-model="greetingDescription"
            class="margin-bottom"
            :contentStyle="editorContentStyle"
            :height="300"
          />
        </div>
        <p
          v-if="!canEdit"
          class="design-comment-text"
          v-html="techComment.greetingDescription"
        />
        <div class="closing margin-bottom">
          <DescriptionTinymceEditor
            v-if="canEditingEditor"
            ref="descriptionEditor"
            :key="activeSubmission.id"
            v-model="description"
            class="comment-text"
            :contentStyle="editorContentStyle"
            :height="200"
          />
        </div>
        <p
          v-if="!canEdit"
          class="design-comment-text"
          v-html="techComment.description"
        />

        <TechCommentsGuidelines />
        <div class="closing margin-bottom">
          <DescriptionTinymceEditor
            v-if="canEditingEditor"
            ref="closingEditor"
            :key="activeSubmission.id"
            v-model="closingDescription"
            :contentStyle="editorContentStyle"
            :height="300"
          />
        </div>
        <p
          v-if="!canEdit"
          class="design-comment-text"
          v-html="techComment.closingDescription"
        />
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, getCurrentInstance, inject, unref } from 'vue-demi'
import TechCommentsGuidelines from './TechCommentsGuidelines'
import { utils as formUtil } from '@/plugin/InputChange'
import * as settingsRequest from '@/api/settings/request'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import {
  hasSubmissionReviewStarted,
  isPendingSubmissionUnderReview,
} from '@/modules/tenantProjects/utils'
import TechCommentsMixin from './TechCommentsMixin'
import DescriptionTinymceEditor from '../DescriptionTinymceEditor.component'
import { DEPENDENCY_KEYS, TAB_TYPE } from '../consts'

export default {
  name: 'TechComments',
  components: {
    DescriptionTinymceEditor,
    TechCommentsGuidelines,
  },
  mixins: [TechCommentsMixin],
  setup () {
    const activedTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    const { proxy } = getCurrentInstance()
    // There is a bug, when the pane is deactivated, the iframe content is removed.
    // It make the TinymceEditor not work.
    // Use `v-if` to force re-rendering the TinymceEditor
    const canEditingEditor = computed(() => proxy.canEdit && unref(activedTabType) === TAB_TYPE.FIT_OUT)
    return {
      canEditingEditor,
    }
  },
  data () {
    return {
      classifiedGuidelines: [],
      dateUtil,
      deep: lodash.get,
      testcontent: '',
      editorContentStyle: '.pmsr-tinymce-body {text-align:justify;word-break:break-word}',
    }
  },
  computed: {
    greetingDescription: {
      get: function () {
        return this.techComment.greetingDescription
      },
      set: function (value) {
        this.updateDescription('greetingDescription', value)
      },
    },
    description: {
      get: function () {
        return this.techComment.description
      },
      set: function (value) {
        this.updateDescription('description', value)
      },
    },
    closingDescription: {
      get: function () {
        return this.techComment.closingDescription
      },
      set: function (value) {
        this.updateDescription('closingDescription', value)
      },
    },
  },
  watch: {
    async needToInitializeTechComment (val) {
      if (!val) { return }
      await this.initializeTechComment()
    },
  },
  destroyed () {
    if (this.techCommentAccordion && Object.keys(this.techCommentAccordion).length > 0) {
      if ($('#tech-comment-accordion').childElementCount > 0) {
        this.techCommentAccordion.destroy()
      }
      delete this.techCommentAccordion
    }
  },
  methods: {
    formateSequenceNumber (str) {
      const [intPart, decPart] = str.split('.')
      return [Number(intPart), Number(decPart)]
    },
    updateDescription (type, desc) {
      if (desc === this.techComment[type]) return
      this.updateFieldByPath({
        path: ['submissions', this.activeSubmissionIndex, 'techComment', type],
        newValue: desc,
      })
      formUtil.setDirty()
    },
    async initOpenClosingForTechComment () {
      const presetOpeningClosing = await settingsRequest.getTemplates(this.isReinstatement ? 'REINSTATEMENT' : 'FITOUT') || []
      let greetingDescription = ''
      let closingDescription = ''
      let description = ''
      presetOpeningClosing.forEach(value => {
        if (value.title === 'Opening') {
          greetingDescription = value.content
        } else if (value.title === 'Closing') {
          closingDescription = value.content
        } else if (value.title === 'Description') {
          description = value.content
        }
      })
      return { greetingDescription, closingDescription, description }
    },
    async getInitTechComment () {
      const { greetingDescription, closingDescription, description } = await this.initOpenClosingForTechComment()
      return {
        startedAt: new Date(),
        greetingDescription,
        closingDescription,
        description,
      }
    },
    async initializeTechComment () {
      const initTechComment = await this.getInitTechComment()
      this.updateCurrentStateSubmissionByPath({
        path: ['techComment'],
        newValue: {
          ...initTechComment,
          ...this.techComment,
        },
      })
    },
    async startTechReview () {
      if (!deep(this.currentTenantProject, 'id')) { return }

      this.hasPendingSubmission
        ? await this.startTechReviewWithPendingSubmission()
        : await this.startTechReviewWithoutPendingSubmission()
    },
    async startTechReviewWithoutPendingSubmission () {
      const priorSubmission = this.currentTenantSubmission
      const initTechComment = await this.getInitTechComment()
      await this.createTenantSubmission({
        projectId: this.currentTenantProject.id,
        submission: {
          techManagedBy: this.currentUser.id,
          techComment: initTechComment,
        },
      })
      await this.loadTenantProject(this.$router.currentRoute.params.id)
      if (!isPendingSubmissionUnderReview(priorSubmission)) {
        EventBus.$emit('tenant_submission_created')
      }
    },
    async startTechReviewWithPendingSubmission () {
      const priorSubmission = this.currentTenantSubmission
      this.updateCurrentStateSubmissionByPath({
        path: ['techManagedBy'],
        newValue: this.currentUser.id,
      })
      await this.initializeTechComment()
      await this.updateCurrentTenantSubmission({})
      await this.loadTenantProject(this.$router.currentRoute.params.id)
      if (!hasSubmissionReviewStarted(priorSubmission)) {
        EventBus.$emit('tenant_submission_assign_batch')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tender/view";

.tenant-project-tech-comment {
  padding: 15px 15px 11px 12px;
  .title-area {
    margin-bottom: 15px;
    display: flex;
    .records {
      flex: 1;
      text-align: right;
    }
  }
  .guideline-title {
    font-size: 14px;
    margin-bottom: 0px;
    font-weight: bold;
    float: left;
  }
  .font-gray {
    display: inline-block;
    font-size: 11px;
    color: #878787;
    font-weight: normal;
    padding-left: 15px;
  }
  .title-middle {
    margin-top: 5px;
  }
}

.margin-bottom {
  margin-bottom: 15px;
}
.comment-text{
  border: solid #e0e1e4 1px;
  margin-bottom: 15px;
  text-align: justify
}
.design-comment-text {
  margin: 6px 0 6px 6px;
  margin-bottom: 15px;
  text-align: justify;
  pre{
    overflow: visible;
  }
}
.start-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-family: Verdana;
  border: solid #415867 1px;
  display: block;
  margin: 0 auto;
  cursor: pointer;
}
.start-btn-wrapper {
  padding: 15px 0 26px 0;
}
.under-review-hint__text, .tech-comment-not-started {
  color: #878787;
  font-size: 12px;
  text-align: center;
  margin: 20px 0;
}
.update-history__upper-text {
  font-style: italic;
  p {
    color: #878787;
    font-size: 11px;
    text-align: right;
    margin-bottom: 0;
  }
}
#tenant-project-tech-comment {
  .greeting, .closing {
    & ::v-deep .w-e-text-container{
      word-break: break-word;
      text-align: justify;
    }
  }
  .closing {
    margin-top: 15px;
  }
}
</style>

<style lang="scss">
#tenant-project-tech-comment {
  .greeting {
    .MsoNormal {
      margin-left: 0;
      font-family: Verdana;
      font-size: 14px;
    }
  }
}
</style>
